/**
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 Jonathan Rowny
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 * https://github.com/jrowny/react-absolute-grid
 *
 */

import React from 'react';
import PropTypes from 'prop-types';

import LayoutManager from './LayoutManager';

class BaseDisplayObject extends React.Component {
    constructor(props) {
        super(props);

        this.state = {};

        this.isDragItem = this.isDragItem.bind(this);
        this.onDrag = this.onDrag.bind(this);
        this.onDragEnd = this.onDragEnd.bind(this);
        this.updateDrag = this.updateDrag.bind(this);
    }

    componentDidMount() {
        if (this.props.dragEnabled) {
            this.domNode.addEventListener('mousedown', this.onDrag);
            this.domNode.addEventListener('touchstart', this.onDrag);
            this.domNode.setAttribute('data-marker', this.props.item[this.props.markerProp]);
        }
    }

    componentWillUnmount() {
        if (this.props.dragEnabled) {
            this.props.dragManager.endDrag();
            this.domNode.removeEventListener('mousedown', this.onDrag);
            this.domNode.removeEventListener('touchstart', this.onDrag);
        }
    }

    onDragEnd() {
        if (this.isDragItem()) {
            // only make changes for the item being dragged and not the items that had to move
            this.props.dragManager.endDrag(
                this.props.item[this.props.markerProp],
                this.props.index
            );
        }
    }

    updateDrag(x, y) {
        //Pause Animation lets our item return to a snapped position without being animated
        let pauseAnimation = false;
        if (!this.props.dragManager.dragItem) {
            pauseAnimation = true;
            setTimeout(() => {
                this.setState({ pauseAnimation: false });
            }, 20);
        }
        this.setState({
            dragX: x,
            dragY: y,
            pauseAnimation: pauseAnimation,
        });
    }

    onDrag(event) {
        if (this.props.dragManager) {
            document.removeEventListener('mouseup', this.onDragEnd);
            document.removeEventListener('touchend', this.onDragEnd);
            this.props.dragManager.startDrag(
                event,
                this.domNode,
                this.props.item,
                this.updateDrag,
                this.onDragEnd
            );
        }
    }

    isDragItem() {
        return (
            this.props.dragManager.dragItem &&
            this.props.dragManager.dragItem[this.props.markerProp] ===
                this.props.item[this.props.markerProp]
        );
    }

    getStyle() {
        const options = {
            itemWidth: this.props.itemWidth,
            itemHeight: this.props.itemHeight,
            verticalMargin: this.props.verticalMargin,
            zoom: this.props.zoom,
        };
        const layout = new LayoutManager(options, this.props.layoutWidth);
        const style = layout.getStyle(this.props.index, this.props.animation);
        //If this is the object being dragged, return a different style
        if (this.isDragItem()) {
            const dragStyle = this.props.dragManager.getStyle(this.state.dragX, this.state.dragY);
            return { ...style, ...dragStyle };
        } else if (this.state && this.state.pauseAnimation) {
            const pauseAnimationStyle = { ...style };
            pauseAnimationStyle.WebkitTransition = 'none';
            pauseAnimationStyle.MozTransition = 'none';
            pauseAnimationStyle.msTransition = 'none';
            pauseAnimationStyle.transition = 'none';
            return pauseAnimationStyle;
        }
        return style;
    }

    render() {
        return (
            <div
                className={this.props.className}
                ref={node => {
                    this.domNode = node;
                }}
                style={this.getStyle()}
            >
                {this.props.children}
            </div>
        );
    }
}

BaseDisplayObject.propTypes = {
    animation: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
    dragEnabled: PropTypes.bool,
    dragManager: PropTypes.object,
    index: PropTypes.number,
    item: PropTypes.object,
    itemHeight: PropTypes.number,
    itemWidth: PropTypes.number,
    itemsLength: PropTypes.number,
    layoutWidth: PropTypes.number,
    markerProp: PropTypes.string,
    style: PropTypes.object,
    verticalMargin: PropTypes.number,
    zoom: PropTypes.number,
};

export default BaseDisplayObject;
